import { Subject } from 'rxjs';
import createDropdownMenu from '@components/dropdownMenu';
import { chevronDownIconSvg } from 'constants/svg-assets';
import { formatNumericInput, formatWithCommas, hasMinMaxConflict, parseDigits } from 'utils/numericInput';

export const SQUARE_FEET_OPTIONS = [
  'Any',
  '500 SqFt',
  '750 SqFt',
  '1,000 SqFt',
  '1,250 SqFt',
  '1,500 SqFt',
  '1,750 SqFt',
  '2,000 SqFt',
  '2,250 SqFt',
  '2,500 SqFt',
  '2,750 SqFt',
  '3,000 SqFt',
  '4,000 SqFt',
  '5,000 SqFt',
  '6,000 SqFt',
  '7,000 SqFt',
  '8,000 SqFt',
  '10,000 SqFt',
];

function parseSquareFeetValue(text) {
  if (!text || text.trim().toLowerCase() === 'any') {
    return null;
  }

  return parseDigits(text);
}

function setSuffixVisible(field, isVisible) {
  const suffix = field.querySelector('[data-suffix]');
  if (!suffix) {
    return;
  }

  if (isVisible) {
    suffix.removeAttribute('hidden');
  } else {
    suffix.setAttribute('hidden', '');
  }
}

function createFieldMarkup(field, automationIdentifier, label) {
  const optionsMarkup = SQUARE_FEET_OPTIONS.map((option) => `
    <li>
      <button type="button"
              class="nhs-c-dropdown__option nhs-c-dropdown__option--button"
              data-dropdown-option
              data-value="${option}">
        ${option}
      </button>
    </li>
  `).join('');

  return `
    <div class="nhs-c-input__wrapper nhs-c-input__wrapper--filter">
      <div class="nhs-c-input__field nhs-c-input__field--outlined nhs-c-input__field--combobox"
           data-square-feet-field="${field}">
        <input type="text"
               aria-label="${label}"
               class="nhs-c-input__field-input"
               value="Any"
               inputmode="numeric"
               autocomplete="off"
               data-dropdown-focus
               data-qa="${automationIdentifier}" />
        <span class="nhs-c-input__combobox-suffix" data-suffix hidden>SqFt</span>
        <button type="button"
                class="nhs-c-input__combobox-toggle"
                aria-label="Toggle ${label.toLowerCase()} options"
                data-dropdown-toggle>
          ${chevronDownIconSvg}
        </button>
        <ul class="nhs-c-dropdown__list nhs-c-dropdown__list--anchored" data-dropdown-list hidden>
          ${optionsMarkup}
        </ul>
      </div>
    </div>
  `;
}

/**
 * Size (SqFt) filter — Min/Max combobox fields: a typeable input plus a static preset menu,
 * defaulting to "Any". The "SqFt" unit is a fixed label (`[data-suffix]`), not part of the input's
 * own value — putting it in the text competed with the toggle button for width and visually
 * truncated longer values.
 *
 * Emits `{ min, max }` whenever either value settles (a preset is picked, or the field is left
 * after typing) — after Min/Max validation: when the minimum ends up greater than the maximum, the
 * Maximum goes back to "Any", regardless of which of the two fields the user edited.
 *
 * Only produces markup (`getHtml`) and binds behavior to the element the calling control created
 * from it (`new SquareFeetFilter(element)`); it never creates DOM itself.
 */
export default class SquareFeetFilter {
  static getSelector() {
    return '[data-square-feet-filter]';
  }

  static getHtml() {
    return `
      <div class="nhs-c-input__group" data-square-feet-filter data-qa="square_feet_filter">
        <p class="nhs-c-input__label">Size (SqFt)</p>
        <div class="nhs-c-input__range">
          ${createFieldMarkup('min', 'square_feet_filter_min', 'Minimum size')}
          <span class="nhs-c-input__range-separator" aria-hidden="true">–</span>
          ${createFieldMarkup('max', 'square_feet_filter_max', 'Maximum size')}
        </div>
      </div>
    `;
  }

  constructor(element) {
    this.element = element;
    this.value$ = new Subject();
    this.listeners = [];
    this.subscriptions = [];

    this.minField = this.element.querySelector('[data-square-feet-field="min"]');
    this.maxField = this.element.querySelector('[data-square-feet-field="max"]');
    this.minInput = this.minField.querySelector('[data-dropdown-focus]');
    this.maxInput = this.maxField.querySelector('[data-dropdown-focus]');

    this.minMenu = createDropdownMenu(this.minField);
    this.maxMenu = createDropdownMenu(this.maxField);

    this.bindField(this.minField, this.minInput, this.minMenu);
    this.bindField(this.maxField, this.maxInput, this.maxMenu);
  }

  addListener(target, type, handler) {
    target.addEventListener(type, handler);
    this.listeners.push({ target, type, handler });
  }

  bindField(field, input, menu) {
    this.addListener(input, 'input', () => {
      const digits = formatNumericInput(input);
      setSuffixVisible(field, digits.length > 0);
    });

    this.addListener(input, 'blur', () => {
      if (!input.value.trim()) {
        input.value = 'Any';
        setSuffixVisible(field, false);
      }
      this.validateAndEmit();
    });

    this.subscriptions.push(
      menu.getObservable().subscribe((value) => {
        const isAny = value === 'Any';
        input.value = isAny ? 'Any' : value.replace(' SqFt', '');
        setSuffixVisible(field, !isAny);
        this.validateAndEmit();
      }),
    );
  }

  validateAndEmit() {
    const conflict = hasMinMaxConflict({
      min: parseSquareFeetValue(this.minInput.value),
      max: parseSquareFeetValue(this.maxInput.value),
    });
    if (conflict) {
      this.maxInput.value = 'Any';
      setSuffixVisible(this.maxField, false);
    }

    this.value$.next({ min: this.minInput.value, max: this.maxInput.value });
  }

  getObservable() {
    return this.value$.asObservable();
  }

  // Syncs the fields to an externally-known value (e.g. the store's current filter, read back
  // when the modal reopens) without going through value$ — this reflects state that already
  // exists, it isn't a new user edit to emit.
  setValue({ min, max }) {
    [[this.minField, this.minInput, min], [this.maxField, this.maxInput, max]].forEach(
      ([field, input, value]) => {
        if (value == null) {
          input.value = 'Any';
          setSuffixVisible(field, false);
          return;
        }
        input.value = formatWithCommas(String(value));
        setSuffixVisible(field, true);
      },
    );
  }

  destroy() {
    this.listeners.forEach(({ target, type, handler }) => target.removeEventListener(type, handler));
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.minMenu.destroy();
    this.maxMenu.destroy();
  }
}
